import { FormEvent, useState } from "react";
import { Link } from "react-router";
import { DiscoverClubHouseTeamResponse } from "libs/apiClient";
import client from "libs/client";
import { parseMessageAttribute } from "libs/utils";
import { _ } from "i18n";
import { useToast } from "ui-kit";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import type { SelectValue } from "ui-kit/src/MultipleSelect/types";
import Modal from "../../../../../assets/js/components/Modal";
import { AgeCategoryOptions, GenderOptions } from "features/discover/types";
import "./admin-ui.scss";
import "./ClubHouseTeamsTable.css";

export function ClubhouseTeamsTable({
	clubId,
	isDiscover,
}: {
	clubId: number;
	isDiscover: boolean;
}) {
	const { addSuccessToast, addErrorToast } = useToast();
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [nameInput, setNameInput] = useState("");
	const [categoryInput, setCategoryInput] = useState<SelectValue>(null);
	const [genderInput, setGenderInput] = useState<SelectValue>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { data: teamsResponse, isLoading: isTeamsLoading } = client.getDiscoverClubHouseTeams.useQuery(
		{ id: clubId },
		{ queryKey: ["discoverClubHouseTeams", clubId] },
	);
	const teams = teamsResponse?.data ?? [];

	const createTeamMutation = client.postDiscoverClubHouseTeam.useMutation({
		onSuccess: () => {
			addSuccessToast(_("form.club.add_team.success"));
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("discoverClubHouseTeams"),
			});
			closeAddModal();
		},
		onError: (error) => {
			addErrorToast(parseMessageAttribute(error) || _("label.team_name_error"));
			setIsSubmitting(false);
		},
	});

	const closeAddModal = () => {
		setIsAddModalOpen(false);
		setNameInput("");
		setCategoryInput(null);
		setGenderInput(null);
		setIsSubmitting(false);
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const selectedCategory = AgeCategoryOptions.find((option) => option.value === categoryInput?.value);
		const selectedGender = GenderOptions.find((option) => option.value === genderInput?.value);

		if (selectedCategory == null || selectedGender == null) {
			addErrorToast(_("form.required.validation.message"));
			return;
		}

		setIsSubmitting(true);
		createTeamMutation.mutate({
			id: clubId,
			body: {
				name: nameInput,
				category: selectedCategory.value,
				gender: selectedGender.value,
			},
		});
	};

	let teamsContent;
	if (isTeamsLoading) {
		teamsContent = <div className="empty-sub-table">Loading...</div>;
	} else if (teams.length === 0) {
		teamsContent = <div className="empty-sub-table">No teams</div>;
	} else {
		teamsContent = (
			<table className="discover-table">
				<thead>
					<tr>
						<th>Team ID</th>
						<th>Name</th>
						<th>Was invited to the clubhouse</th>
					</tr>
				</thead>
				<tbody>
					{teams.map((team: DiscoverClubHouseTeamResponse) => (
						<tr key={team.id}>
							<td>{team.id}</td>
							<td>
								<Link to={`/discover/team/${team.id}`}>
									<strong>{team.name}</strong>
								</Link>
							</td>
							<td>
								{team.invited !== null && (team.invited ? "Yes" : "No")}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		);
	}

	return (
		<>
			<div style={{ marginBottom: "1rem", display: "flex", justifyContent: "flex-end" }}>
				<button
					type="button"
					className="admin-button admin-button--primary"
					disabled={isDiscover}
					onClick={() => setIsAddModalOpen(true)}
				>
					{_("filter.add_team")}
				</button>
			</div>

			{teamsContent}

			{isAddModalOpen && (
				<Modal
					show={true}
					title={_("filter.add_team")}
					size="sm"
					onClose={() => isSubmitting === false && closeAddModal()}
				>
					<form className="clubhouse-add-team-form" onSubmit={handleSubmit}>
						<div className="clubhouse-add-team-form__field">
							<label className="clubhouse-add-team-form__label" htmlFor="clubhouse-team-name">
								{_("app.coach.match.team_sheet_player_name_header")}{" "}
								<span className="clubhouse-add-team-form__required">*</span>
							</label>
							<input
								id="clubhouse-team-name"
								className="clubhouse-add-team-form__input"
								type="text"
								value={nameInput}
								onChange={(event) => setNameInput(event.target.value)}
								required
								disabled={isSubmitting}
							/>
						</div>
						<div className="clubhouse-add-team-form__field">
							<label className="clubhouse-add-team-form__label">
								{_("form_search_player_game.category")}{" "}
								<span className="clubhouse-add-team-form__required">*</span>
							</label>
							<SelectComponent
								placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
								items={AgeCategoryOptions}
								value={categoryInput}
								handleChange={setCategoryInput}
								isDisabled={isSubmitting}
								isCleareable={false}
								menuPortalTarget
							/>
						</div>
						<div className="clubhouse-add-team-form__field">
							<label className="clubhouse-add-team-form__label">
								{_("form_search_player_game.gender")}{" "}
								<span className="clubhouse-add-team-form__required">*</span>
							</label>
							<SelectComponent
								placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
								items={GenderOptions}
								value={genderInput}
								handleChange={setGenderInput}
								isDisabled={isSubmitting}
								isCleareable={false}
								menuPortalTarget
							/>
						</div>
						<div className="admin-buttons admin-buttons--right">
							<button
								type="submit"
								className="admin-button admin-button--primary"
								disabled={isSubmitting}
							>
								{_("btn.save")}
							</button>
							<button
								type="button"
								className="admin-button admin-button--secondary"
								disabled={isSubmitting}
								onClick={closeAddModal}
							>
								{_("btn.cancel")}
							</button>
						</div>
					</form>
				</Modal>
			)}
		</>
	);
}
